import { yupResolver } from '@hookform/resolvers/yup'
import useGoogleTracking from '@hooks/useGoogleTracking'
import useHandleCouponOrder from '@hooks/useHandleCouponOrder'
import Link from 'next/link'
import React from 'react'
import { type ReactNode, useState } from 'react'
import { type Resolver, useForm } from 'react-hook-form'
import { useTheme } from 'styled-components'
import * as yup from 'yup'

import {
  ButtonModifier,
  ButtonTypes,
  IconAligns,
} from '@components/atoms/Button/Button'
import IconArrowRight from '@components/atoms/Icons/IconArrowRight'
import IconCircleX from '@components/atoms/Icons/IconCircleX'
import Text from '@components/atoms/Text'
import { TextVariants } from '@components/atoms/Text/Text'

import { RoutesSunpoint } from '@definitions/routes/RoutesSunpoint'
import { Coupon, Studio } from '@definitions/types/symfonyTypesd'

import {
  Checkboxes,
  CloseIconWrapper,
  FormResult,
  InputButton,
  Root,
  StyledButton,
  StyledButtonDesktop,
  StyledButtonMobile,
  StyledCouponItem,
  StyledInputCheckbox,
  StyledInputText,
  StyledLabel,
} from './CouponItem.styles'

export interface FormType {
  email: string
  privacy: boolean
  newsletter: boolean
}

function Form({
  formId,
  onClose,
  coupon,
  studio,
}: {
  formId: string
  onClose: () => void
  coupon: Coupon
  studio: Studio
}) {
  const [loading, setLoading] = useState<boolean>(false)
  const [formSend, setFormSend] = useState<boolean>(false)
  const [result, setResult] = useState<string | null>(null)
  const { register, handleSubmit, formState } = useForm<FormType>({
    resolver: yupResolver(
      yup.object().shape({
        email: yup
          .string()
          .email('Dies ist keine valide E-Mail-Adresse')
          .required('Dies ist ein Pflichtfeld.')
          .test(
            'is-valid-email',
            'Dies ist keine valide E-Mail-Adresse',
            (value) => {
              if (!value) return false
              return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
            }
          ),
        privacy: yup
          .boolean()
          .oneOf(
            [true],
            'Bitte lese und akzeptiere unsere Datenschutzerklärung um fortzufahren.'
          ),
        newsletter: yup.boolean(),
      })
    ) as Resolver<FormType>,
  })

  const { trackDownloadCoupon } = useGoogleTracking()

  //temp solution for sending mail; a mail to user with test print is maybe required

  const { handleCouponOrder, handleCouponOrderError } = useHandleCouponOrder(
    (status) => {
      setResult(status)
    },
    () => {
      setResult('error')
      console.info('coupon download failed')
      console.error(handleCouponOrderError)
    }
  )

  const theme: any = useTheme()

  const onSubmit = async (data: FormType) => {
    setLoading(true)
    await handleCouponOrder(
      coupon,
      theme.key[0].toUpperCase() + theme.key.slice(1),
      studio,
      data.email
    )
    trackDownloadCoupon(studio, coupon.title)
    setLoading(false)
    setFormSend(true)
  }

  const renderResult = (status: string | null) => {
    if (status === 'success') {
      return (
        <>
          <Text variant={TextVariants.lead}>
            <b>Vielen Dank!</b>
          </Text>
          <Text variant={TextVariants.paragraph}>
            Der Coupon wird in Kürze an deine E-Mail-Adresse gesendet.
          </Text>
        </>
      )
    } else if (result === 'repeated_use') {
      return (
        <>
          <Text variant={TextVariants.lead}>
            <b>Fehler</b>
          </Text>
          <Text variant={TextVariants.paragraph}>
            Du kannst diesen Coupon nur einmal in der Woche herunterladen.
          </Text>
        </>
      )
    } else {
      return (
        <>
          <Text variant={TextVariants.lead}>
            <b>Es ist ein Fehler aufgetreten</b>
          </Text>
          <Text variant={TextVariants.paragraph}>
            Es ist ein Fehler aufgetreten
          </Text>
        </>
      )
    }
  }

  return (
    <Root>
      <CloseIconWrapper onClick={onClose}>
        <IconCircleX />
      </CloseIconWrapper>
      {!formSend && (
        <form
          action="./"
          onError={(error) => console.log(error)}
          onSubmit={handleSubmit(onSubmit)}
        >
          <StyledLabel htmlFor="coupon-code">
            E-Mail-Adresse angeben und Coupon zugesendet bekommen:
          </StyledLabel>
          <InputButton>
            <StyledInputText
              register={register('email')}
              forId={`${formId}-email`}
              label="E-Mail-Adresse"
              required
              error={formState.errors.email ?? false}
              errorText={formState.errors?.email?.message}
            />
            <StyledButtonDesktop
              modifier={ButtonModifier.TERTIARY}
              buttonType={ButtonTypes.SUBMIT}
              disabled={formState.isSubmitting}
              loading={loading}
              icon={<IconArrowRight />}
              iconAlign={IconAligns.RIGHT}
            >
              Coupon anfordern
            </StyledButtonDesktop>
          </InputButton>
          <Checkboxes>
            <StyledInputCheckbox
              register={register('privacy')}
              forId={`${formId}-privacy`}
              error={formState.errors.privacy ?? false}
              errorText={formState.errors?.privacy?.message}
              label={
                <>
                  Ja, ich habe die&nbsp;
                  <Link href={RoutesSunpoint.SUNPOINT__DATENSCHUTZ}>
                    Datenschutzerklärung
                  </Link>
                  &nbsp;gelesen
                </>
              }
            />
            <StyledInputCheckbox
              register={register('newsletter')}
              forId={`${formId}-newsletter`}
              error={formState.errors.newsletter ?? false}
              errorText={formState.errors?.newsletter?.message}
              label={
                <>
                  SUNPOINT Newsletter: Ja, ich möchte wöchentlich über aktuelle
                  Trends, Angebote und Gutscheine per E-Mail informiert werden.
                  Diese Einwilligung kann ich jederzeit gemäß &nbsp;
                  <Link href={RoutesSunpoint.SUNPOINT__DATENSCHUTZ}>
                    Datenschutzerklärung
                  </Link>
                  &nbsp; widerrufen.
                </>
              }
            />
          </Checkboxes>
          <StyledButtonMobile
            modifier={ButtonModifier.TERTIARY}
            buttonType={ButtonTypes.SUBMIT}
            disabled={formState.isSubmitting}
            loading={loading}
            icon={<IconArrowRight />}
            iconAlign={IconAligns.RIGHT}
          >
            Coupon anfordern
          </StyledButtonMobile>
        </form>
      )}
      {formSend && (
        <FormResult success={result === 'success'}>
          {renderResult(result)}
        </FormResult>
      )}
    </Root>
  )
}

export function CouponItem({
  children,
  formId,
  coupon,
  studio,
}: {
  children: ReactNode
  formId: string
  coupon: Coupon
  studio: Studio
}) {
  const [showForm, setShowForm] = useState(false)

  return (
    <StyledCouponItem onClick={() => !showForm && setShowForm(true)}>
      {showForm ? (
        <Form
          formId={formId}
          onClose={() => setShowForm(false)}
          coupon={coupon}
          studio={studio}
        />
      ) : (
        children
      )}
    </StyledCouponItem>
  )
}
